/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to get access to router context.
 */

/*
 * Used to style components
 */
import { LinearProgress } from '@mui/material';

/**
 * Used for the basic page layout.
 */
import {
    CrfCondition,
    FormLookupsContext,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    Select,
    Text
} from '@ngt/opms';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to type patient state.
 */
import { useMemo } from 'react';
import useInvestigatorLookup, { INVESTIGATOR_LOOKUP_OTHER } from '../../../hooks/lookups/useInvestigatorLookup';
import { StartConsent } from '../../../api/dtos';
import { ConditionMode } from '@ngt/forms-core';

const investigatorOther = ({ formState }: ICrfConditionParameters<StartConsent>) => formState?.value?.consentEvent.investigator === INVESTIGATOR_LOOKUP_OTHER;

export interface IInvestigatorSelectorProps {
    propertyPath: string;
    otherPropertyPath?: string;
    institutionCode?: string;
    hideOther?: boolean
}

const InvestigatorSelector: React.FunctionComponent<IInvestigatorSelectorProps> = ({
    propertyPath,
    institutionCode,
    otherPropertyPath,
    hideOther
}) => {
    const { data: investigatorLookup, loading } = useInvestigatorLookup(propertyPath, institutionCode, hideOther);

    const investigators = useMemo(() => [investigatorLookup], [investigatorLookup])

    return (
        <FormLookupsContext.Provider value={investigators}>
            {
                loading ? 
                    <LinearProgress color='primary' /> : 
                    <GroupedFields>
                        <GroupedField 
                            propertyPath={propertyPath}
                            component={Select}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 6,
                                    md: 6,
                                    lg: 6,
                                    xl: 6
                                }
                            }}
                        />
                    </GroupedFields>
            }    
            {
                otherPropertyPath && (
                    <CrfCondition
                        mode={ConditionMode.Show}
                        condition={investigatorOther}
                        subscription={{ value: true }}
                    >
                        <GroupedField
                            propertyPath={otherPropertyPath}
                            component={Text}
                            FieldGridProps={{
                                size: {
                                    xs: 12,
                                    sm: 6,
                                    md: 6,
                                    lg: 6,
                                    xl: 6
                                }
                            }}
                        />
                    </CrfCondition>
                )
            }
        </FormLookupsContext.Provider>
    );
}

export default InvestigatorSelector;